import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { EmergencyEmptyComponent } from './core/components/emergency-empty/emergency-empty.component';
import { Layouts } from './core/layouts/layouts.enum';
import { AuthenticationGuard } from './modules/authentication/guards/authentication.guard';
import { CallbackComponent } from './modules/authentication/pages/callback/callback.component';
import { LoginComponent } from './modules/authentication/pages/login/login.component';
import { LogoutComponent } from './modules/authentication/pages/logout/logout.component';
import { PasswordResetComponent } from './modules/authentication/pages/password-reset/password-reset.component';

export const routes: Routes = [
  { path: '', component: EmergencyEmptyComponent, canActivate: [AuthenticationGuard.CheckLogin()] },
  // For redirecting to specific error pages...
  {
    path: 'error',
    loadChildren: () => import('./modules/error-pages/error-pages.module').then((m) => m.ErrorPagesModule),
  },
  {
    path: 'round',
    loadChildren: () => import('./modules/round/round.module').then((module) => module.RoundModule),
    data: { breadcrumb: $localize`Rondes` },
    canActivate: [AuthenticationGuard.CheckLogin()],
  },
  {
    path: 'configuration',
    data: { layout: Layouts.DEFAULT, breadcrumb: $localize`Configuratie` },
    loadChildren: () => import('./modules/configuration/configuration.module').then((m) => m.ConfigurationModule),
    canActivate: [AuthenticationGuard.CheckLogin()],
  },
  {
    path: 'person',
    data: { layout: Layouts.DEFAULT, breadcrumb: $localize`Personen` },
    loadChildren: () => import('./modules/person/person.module').then((m) => m.PersonModule),
    canActivate: [AuthenticationGuard.CheckLogin()],
  },
  {
    path: 'vehicle',
    data: { layout: Layouts.DEFAULT, breadcrumb: $localize`Voertuigen` },
    loadChildren: () => import('./modules/vehicle/vehicle.module').then((m) => m.VehicleModule),
    canActivate: [AuthenticationGuard.CheckLogin()],
  },
  {
    path: 'interventionmedia/:id',
    data: { layout: Layouts.DEFAULT, breadcrumb: $localize`Interventie media` },
    loadComponent: () =>
      import('./modules/intervention-media/intervention-media.component').then((m) => m.InterventionMediaComponent),
    canActivate: [AuthenticationGuard.CheckLogin()],
  },
  // NOTE: intervention-map is used by In The Field (ITF) and must remain unguarded
  {
    path: 'intervention-map',
    data: { breadcrumb: $localize`Kaart`, layout: Layouts.BORDERLESS, isMobile: true },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./modules/intervention-map/intervention-map.component').then((m) => m.InterventionMapComponent),
      },
      {
        path: ':id',
        loadComponent: () =>
          import('./modules/intervention-map/intervention-map.component').then((m) => m.InterventionMapComponent),
        data: { breadcrumb: $localize`Interventiekaart`, layout: Layouts.BORDERLESS },
      },
    ],
  },
  {
    path: 'map',
    data: { breadcrumb: $localize`Kaart`, layout: Layouts.BORDERLESS, isMobile: false },
    canActivate: [AuthenticationGuard.CheckLogin()],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./modules/intervention-map/intervention-map.component').then((m) => m.InterventionMapComponent),
      },
      {
        path: ':id',
        loadComponent: () =>
          import('./modules/intervention-map/intervention-map.component').then((m) => m.InterventionMapComponent),
      },
      {
        path: 'config/:type',
        loadComponent: () =>
          import('./modules/map/components/config-based-map/config-based-map.component').then(
            (m) => m.ConfigBasedMapComponent
          ),
      },
    ],
  },
  {
    path: 'intervention',
    data: { layout: Layouts.DEFAULT, breadcrumb: $localize`Interventies` },
    loadChildren: () => import('./modules/intervention/intervention.module').then((m) => m.InterventionModule),
    canActivate: [AuthenticationGuard.CheckLogin()],
  },
  {
    path: 'dashboard',
    data: { breadcrumb: $localize`Dashboard`, layout: Layouts.DASHBOARD },
    loadChildren: () => import('./modules/dashboard/dashboard.module').then((m) => m.DashboardModule),
    canActivate: [AuthenticationGuard.CheckPermissions(['Web.DashboardUse'])],
  },
  {
    path: 'digital-signage',
    data: { breadcrumb: $localize`Infoscherm`, layout: Layouts.KIOSK },
    loadChildren: () => import('./modules/digital-signage/digital-signage.module').then((m) => m.DigitalSignageModule),
    canActivate: [AuthenticationGuard.CheckLogin()],
  },
  // Unguarded route for token-based kiosk display (uses digital-signage-token query param)
  {
    path: 'digital-signage-display',
    data: { layout: Layouts.KIOSK },
    loadChildren: () => import('./modules/digital-signage/digital-signage.module').then((m) => m.DigitalSignageModule),
  },
  {
    path: 'information',
    data: { breadcrumb: $localize`Informatie`, layout: Layouts.DEFAULT },
    loadChildren: () => import('./modules/information/information.module').then((d) => d.InformationModule),
    canActivate: [AuthenticationGuard.CheckLogin()],
  },
  {
    path: 'incident-flows',
    data: { breadcrumb: $localize`Interventie formulieren`, layout: Layouts.DEFAULT },
    loadChildren: () => import('./modules/incident-flows/incident-flows.module').then((m) => m.IncidentFlowsModule),
    canActivate: [AuthenticationGuard.CheckLogin()],
  },
  {
    path: 'debug',
    loadChildren: () => import('./modules/debugging/debugging.module').then((m) => m.DebuggingModule),
    data: { breadcrumb: 'Debug' },
    canActivate: [AuthenticationGuard.CheckLogin()],
  },
  {
    path: 'login',
    data: { layout: Layouts.FULL },
    component: LoginComponent,
  },
  {
    path: 'login/password-reset',
    data: { layout: Layouts.FULL },
    component: PasswordResetComponent,
  },
  { path: 'logout', component: LogoutComponent, data: { layout: Layouts.FULL } },
  { path: 'authorization-code/callback', component: CallbackComponent, data: { layout: Layouts.FULL } },
  // IMPORTANT NOTE: If you include routes directly from lazy loaded modules,
  // lazy loading does not work anymore! Use the module specific routing module instead!
  // NOTE: '**' PATH NEEDS TO BE AT THE END!
  { path: '**', redirectTo: 'error/not-found' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
